import React, { useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { KeyRound } from "lucide-react";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { Button } from "components/Graphs/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import { Input } from "components/Graphs/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import type {
  LaunchableManagedSecretReference,
  LaunchableParameter,
  LaunchableParameterBinding,
} from "server";
import type { ManagedSecretSummary } from "server/devplane/managedSecrets/api";
import { useManagedSecretVersionsQuery } from "server/devplane/managedSecrets/queries";
import { launchableDeployControlClassName } from "components/Launchables/DeployRedesign/shared/configuration/controlStyles";

const EMPTY_SELECT_VALUE = "__launchable_parameter_empty__";
const CREATE_SECRET_SELECT_VALUE = "__launchable_parameter_create_secret__";
const PARAMETER_TYPES = ["text", "choice"] as const;
type LaunchableParameterType = (typeof PARAMETER_TYPES)[number];

const getLaunchableParameterType = (
  parameter: LaunchableParameter,
): LaunchableParameterType => (parameter.choice ? "choice" : "text");

export const launchableParameterSupportsManagedSecret = (
  parameter: LaunchableParameter,
): boolean => getLaunchableParameterType(parameter) !== "choice";

const getLaunchableParameterChoiceValues = (
  parameter: LaunchableParameter,
): string[] =>
  (parameter.choice?.choices || [])
    .map((choice) => choice.trim())
    .filter((choice) => choice !== "");

const getLaunchableParameterDefaultValue = (
  parameter: LaunchableParameter,
): string => {
  if (getLaunchableParameterType(parameter) === "choice") {
    return parameter.choice?.defaultValue?.trim() || "";
  }
  return parameter.text?.defaultValue || "";
};

const normalizeLaunchableParameters = (
  parameters: LaunchableParameter[],
): LaunchableParameter[] =>
  parameters.map((parameter) => {
    const normalizedParameter: LaunchableParameter = {
      name: parameter.name.trim(),
      description: parameter.description?.trim() || undefined,
      required: parameter.required,
    };

    if (getLaunchableParameterType(parameter) === "choice") {
      const choices = getLaunchableParameterChoiceValues(parameter);
      const defaultValue = parameter.choice?.defaultValue?.trim();
      if (choices.length > 0) {
        normalizedParameter.choice = {
          choices,
          defaultValue: defaultValue || undefined,
        };
      }
      return normalizedParameter;
    }

    if (parameter.text?.defaultValue) {
      normalizedParameter.text = {
        defaultValue: parameter.text.defaultValue,
      };
    }

    return normalizedParameter;
  });

export const orderLaunchableParametersForPresentation = (
  parameters: LaunchableParameter[],
): LaunchableParameter[] => {
  const normalizedParameters = normalizeLaunchableParameters(parameters);

  return [
    ...normalizedParameters.filter((parameter) => parameter.required),
    ...normalizedParameters.filter((parameter) => !parameter.required),
  ];
};

interface LaunchableParameterBindingFormProps {
  parameters: LaunchableParameter[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  secretSelections?: Record<string, LaunchableManagedSecretReference>;
  onSecretSelectionsChange?: (
    selections: Record<string, LaunchableManagedSecretReference>,
  ) => void;
  availableSecrets?: ManagedSecretSummary[];
  secretsLoading?: boolean;
  organizationId?: string;
  onCreateSecret?: () => void;
  disabled?: boolean;
  hideHeader?: boolean;
  showValidationErrors?: boolean;
  compactManagedSecretPicker?: boolean;
}

export const getLaunchableParameterEffectiveValue = (
  parameter: LaunchableParameter,
  values: Record<string, string>,
): string =>
  Object.prototype.hasOwnProperty.call(values, parameter.name)
    ? values[parameter.name] || ""
    : getLaunchableParameterDefaultValue(parameter);

export const getLaunchableParameterBindings = (
  parameters: LaunchableParameter[],
  values: Record<string, string>,
  secretSelections: Record<string, LaunchableManagedSecretReference> = {},
): LaunchableParameterBinding[] =>
  normalizeLaunchableParameters(parameters)
    .map((parameter) => {
      const managedSecret = launchableParameterSupportsManagedSecret(parameter)
        ? secretSelections[parameter.name]
        : undefined;
      if (managedSecret?.secretId) {
        return {
          name: parameter.name,
          managedSecret: {
            secretId: managedSecret.secretId,
            ...(managedSecret.versionId
              ? { versionId: managedSecret.versionId }
              : {}),
          },
        };
      }
      return {
        name: parameter.name,
        value: getLaunchableParameterEffectiveValue(parameter, values),
      };
    })
    .filter((binding) => binding.managedSecret || binding.value !== "");

export const hasMissingRequiredParameterBindings = (
  parameters: LaunchableParameter[],
  values: Record<string, string>,
  secretSelections: Record<string, LaunchableManagedSecretReference> = {},
): boolean =>
  normalizeLaunchableParameters(parameters).some(
    (parameter) =>
      parameter.required &&
      !(
        launchableParameterSupportsManagedSecret(parameter) &&
        secretSelections[parameter.name]?.secretId
      ) &&
      !getLaunchableParameterEffectiveValue(parameter, values),
  );

interface ManagedSecretVersionSelectProps {
  selection: LaunchableManagedSecretReference;
  latestVersionId: string;
  latestVersionNumber?: bigint;
  parameterName: string;
  compact?: boolean;
  disabled?: boolean;
  onChange: (selection: LaunchableManagedSecretReference) => void;
}

const ManagedSecretVersionSelect: React.FC<ManagedSecretVersionSelectProps> = ({
  selection,
  latestVersionId,
  latestVersionNumber,
  parameterName,
  compact,
  disabled,
  onChange,
}) => {
  const versionsQuery = useManagedSecretVersionsQuery(selection.secretId);

  if (compact) {
    const versions = versionsQuery.data ?? [];
    const selectedVersionId = selection.versionId || latestVersionId;
    const selectedVersion = versions.find(
      (version) => version.id === selectedVersionId,
    );
    const selectedVersionNumber =
      selectedVersion?.versionNumber ??
      (selectedVersionId === latestVersionId ? latestVersionNumber : undefined);
    const isLatestVersion =
      Boolean(latestVersionId) && selectedVersionId === latestVersionId;

    return (
      <div className="relative -top-1 px-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="noshow"
              className="group h-10 justify-start gap-1 rounded-md text-[10px] font-normal leading-4 text-secondary disabled:opacity-50"
              disabled={
                disabled || versionsQuery.isLoading || versions.length === 0
              }
            >
              <span className="sr-only">
                Choose a secret version for {parameterName}:{" "}
              </span>
              <span>
                {selectedVersionNumber !== undefined
                  ? `Version ${selectedVersionNumber.toString()}`
                  : versionsQuery.isLoading
                    ? "Loading version…"
                    : "Version unavailable"}
              </span>
              {isLatestVersion && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="text-highlight">Latest</span>
                </>
              )}
              <ChevronDownIcon
                className="ml-1 h-3 w-3 shrink-0 text-zinc-500 transition-transform duration-150 group-data-[state=open]:rotate-180"
                aria-hidden="true"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="bottom"
            className="max-h-60 overflow-y-auto"
          >
            <DropdownMenuLabel>Secret version</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={selectedVersionId}
              onValueChange={(versionId) =>
                onChange({
                  secretId: selection.secretId,
                  versionId,
                })
              }
              aria-label="Secret version"
            >
              {versions.map((version) => (
                <DropdownMenuRadioItem
                  key={version.id}
                  value={version.id}
                  indicator="check"
                  hint={version.id === latestVersionId ? "Latest" : undefined}
                >
                  Version {version.versionNumber.toString()}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-secondary">Version</label>
      <Select
        value={selection.versionId}
        onValueChange={(versionId) =>
          onChange({
            secretId: selection.secretId,
            versionId,
          })
        }
        disabled={disabled || versionsQuery.isLoading}
      >
        <SelectTrigger
          className={`${launchableDeployControlClassName} px-3 py-2`}
        >
          <SelectValue placeholder="Select a version" />
        </SelectTrigger>
        <SelectContent>
          {(versionsQuery.data ?? []).map((version) => (
            <SelectItem key={version.id} value={version.id}>
              <span className="flex items-center gap-2">
                <span>Version {version.versionNumber.toString()}</span>
                {version.id === latestVersionId && (
                  <span className="rounded-full border border-highlight/30 bg-highlight/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-highlight">
                    Latest
                  </span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export const LaunchableParameterBindingForm: React.FC<
  LaunchableParameterBindingFormProps
> = ({
  parameters,
  values,
  onChange,
  secretSelections = {},
  onSecretSelectionsChange,
  availableSecrets = [],
  secretsLoading = false,
  organizationId,
  onCreateSecret,
  disabled,
  hideHeader = false,
  showValidationErrors = false,
  compactManagedSecretPicker = false,
}) => {
  const normalizedParameters =
    orderLaunchableParametersForPresentation(parameters);
  const [touchedParameters, setTouchedParameters] = useState<
    Record<string, boolean>
  >({});
  const [secretModeParameters, setSecretModeParameters] = useState<
    Record<string, boolean>
  >({});

  if (normalizedParameters.length === 0) {
    return null;
  }

  return (
    <div
      className={
        hideHeader
          ? "flex flex-col gap-4 px-5 py-4"
          : "flex flex-col gap-3 border-t border-zinc-800 px-4 py-3"
      }
    >
      {!hideHeader && (
        <div>
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-highlight" />
            <p className="text-sm font-medium text-secondary">Setup values</p>
          </div>
          <p className="mt-1 text-xs text-secondary">
            These values are passed as environment variables while the
            Launchable starts.
          </p>
        </div>
      )}

      {normalizedParameters.map((parameter) => {
        const choiceValues = Array.from(
          new Set(getLaunchableParameterChoiceValues(parameter)),
        );
        const isChoiceParameter =
          !launchableParameterSupportsManagedSecret(parameter);
        const value = getLaunchableParameterEffectiveValue(parameter, values);
        const secretSelection = isChoiceParameter
          ? undefined
          : secretSelections[parameter.name];
        const selectedSecret = availableSecrets.find(
          (secret) => secret.id === secretSelection?.secretId,
        );
        const usesSecret =
          Boolean(secretSelection?.secretId) ||
          Boolean(secretModeParameters[parameter.name]);
        const isMissingRequired =
          parameter.required && !value && !secretSelection?.secretId;

        const updateSecretSelection = (
          selection?: LaunchableManagedSecretReference,
        ) => {
          if (!onSecretSelectionsChange) return;
          const nextSelections = { ...secretSelections };
          if (selection?.secretId) {
            nextSelections[parameter.name] = selection;
          } else {
            delete nextSelections[parameter.name];
          }
          onSecretSelectionsChange(nextSelections);
          setTouchedParameters((current) => ({
            ...current,
            [parameter.name]: true,
          }));
        };

        return (
          <div key={parameter.name} className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              <label
                htmlFor={`launchable-parameter-binding-${parameter.name}`}
                className="flex items-center justify-between gap-2 text-xs font-medium text-secondary"
              >
                <span className="font-mono text-primary">
                  {parameter.name}
                  {parameter.required && (
                    <TooltipPrimitives.Provider>
                      <TooltipPrimitives.Root delayDuration={0}>
                        <TooltipPrimitives.Trigger asChild>
                          <span
                            className="ml-1 cursor-help text-red-500"
                            aria-label="Required to deploy"
                            tabIndex={0}
                          >
                            *
                          </span>
                        </TooltipPrimitives.Trigger>
                        <TooltipPrimitives.Portal>
                          <TooltipPrimitives.Content
                            side="top"
                            sideOffset={4}
                            className="z-50 max-w-xs rounded-md bg-zinc-800 px-3 py-2 text-xs text-white shadow-md"
                          >
                            Required to deploy
                            <TooltipPrimitives.Arrow className="fill-zinc-800" />
                          </TooltipPrimitives.Content>
                        </TooltipPrimitives.Portal>
                      </TooltipPrimitives.Root>
                    </TooltipPrimitives.Provider>
                  )}
                </span>
              </label>
              {onSecretSelectionsChange && !isChoiceParameter && (
                <button
                  type="button"
                  className="text-xs font-medium text-highlight hover:underline disabled:text-secondary"
                  disabled={disabled}
                  onClick={() => {
                    if (usesSecret) {
                      setSecretModeParameters((current) => ({
                        ...current,
                        [parameter.name]: false,
                      }));
                      updateSecretSelection();
                      const nextValues = { ...values };
                      delete nextValues[parameter.name];
                      onChange(nextValues);
                    } else {
                      setSecretModeParameters((current) => ({
                        ...current,
                        [parameter.name]: true,
                      }));
                      onChange({ ...values, [parameter.name]: "" });
                    }
                  }}
                >
                  {usesSecret ? "Enter a value" : "Use a secret"}
                </button>
              )}
            </div>
            {parameter.description && (
              <p className="text-xs text-secondary">{parameter.description}</p>
            )}
            {usesSecret ? (
              <div
                className={
                  compactManagedSecretPicker
                    ? "space-y-0"
                    : "space-y-3 rounded-md border border-zinc-800 bg-zinc-950/30 p-3"
                }
              >
                {secretsLoading ? (
                  <p className="text-xs text-secondary">Loading secrets…</p>
                ) : availableSecrets.length === 0 &&
                  !(compactManagedSecretPicker && onCreateSecret) ? (
                  <p className="text-xs text-secondary">
                    No accessible secrets in this organization.{" "}
                    {organizationId && (
                      <a
                        href={`/org/${organizationId}/secrets`}
                        className="text-highlight hover:underline"
                      >
                        Manage secrets
                      </a>
                    )}
                  </p>
                ) : (
                  <>
                    <div className="space-y-1">
                      {!compactManagedSecretPicker && (
                        <label className="text-xs font-medium text-secondary">
                          Secret
                        </label>
                      )}
                      <Select
                        value={secretSelection?.secretId || EMPTY_SELECT_VALUE}
                        onValueChange={(secretId) => {
                          if (secretId === CREATE_SECRET_SELECT_VALUE) {
                            onCreateSecret?.();
                            return;
                          }
                          const nextSecret = availableSecrets.find(
                            (secret) => secret.id === secretId,
                          );
                          updateSecretSelection(
                            secretId === EMPTY_SELECT_VALUE
                              ? undefined
                              : {
                                  secretId,
                                  ...(nextSecret?.latestVersionId
                                    ? {
                                        versionId: nextSecret.latestVersionId,
                                      }
                                    : {}),
                                },
                          );
                        }}
                        disabled={disabled}
                      >
                        <SelectTrigger
                          id={`launchable-parameter-binding-${parameter.name}`}
                          className={`${launchableDeployControlClassName} px-3 py-2`}
                          hasError={
                            isMissingRequired &&
                            (showValidationErrors ||
                              touchedParameters[parameter.name])
                          }
                        >
                          <SelectValue placeholder="Select a secret" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={EMPTY_SELECT_VALUE}>
                            Select a secret
                          </SelectItem>
                          {availableSecrets.map((secret) => (
                            <SelectItem key={secret.id} value={secret.id}>
                              {secret.name}
                            </SelectItem>
                          ))}
                          {compactManagedSecretPicker && onCreateSecret && (
                            <>
                              <SelectSeparator />
                              <SelectItem
                                value={CREATE_SECRET_SELECT_VALUE}
                                className="font-medium text-highlight"
                                textValue="Create Secret in Org Settings"
                              >
                                <span className="flex items-center gap-1.5">
                                  <span>
                                    Create Secret{" "}
                                    <span className="sr-only">
                                      in Org Settings; opens in a new tab
                                    </span>
                                  </span>
                                  <ArrowTopRightOnSquareIcon
                                    className="h-3.5 w-3.5 shrink-0"
                                    aria-hidden="true"
                                  />
                                </span>
                              </SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    {secretSelection?.secretId && (
                      <ManagedSecretVersionSelect
                        selection={secretSelection}
                        latestVersionId={selectedSecret?.latestVersionId || ""}
                        latestVersionNumber={
                          selectedSecret?.latestVersionNumber
                        }
                        parameterName={parameter.name}
                        compact={compactManagedSecretPicker}
                        disabled={disabled}
                        onChange={updateSecretSelection}
                      />
                    )}
                  </>
                )}
              </div>
            ) : isChoiceParameter ? (
              <Select
                value={value || EMPTY_SELECT_VALUE}
                onValueChange={(selectValue) => {
                  onChange({
                    ...values,
                    [parameter.name]:
                      selectValue === EMPTY_SELECT_VALUE ? "" : selectValue,
                  });
                  setTouchedParameters((current) => ({
                    ...current,
                    [parameter.name]: true,
                  }));
                }}
                disabled={disabled}
              >
                <SelectTrigger
                  id={`launchable-parameter-binding-${parameter.name}`}
                  className={`${launchableDeployControlClassName} px-3 py-2`}
                  hasError={
                    isMissingRequired &&
                    (showValidationErrors || touchedParameters[parameter.name])
                  }
                >
                  <SelectValue placeholder={`Value for ${parameter.name}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_SELECT_VALUE}>
                    {parameter.required ? "Select a value" : "No value"}
                  </SelectItem>
                  {choiceValues.map((choice) => (
                    <SelectItem key={choice} value={choice}>
                      {choice}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={`launchable-parameter-binding-${parameter.name}`}
                type="password"
                value={value}
                onChange={(event) =>
                  onChange({ ...values, [parameter.name]: event.target.value })
                }
                onBlur={() =>
                  setTouchedParameters({
                    ...touchedParameters,
                    [parameter.name]: true,
                  })
                }
                placeholder={`Value for ${parameter.name}`}
                disabled={disabled}
                hasError={
                  isMissingRequired &&
                  (showValidationErrors || touchedParameters[parameter.name])
                }
                inputClassName={`${launchableDeployControlClassName} px-3 py-2 pr-10 font-mono`}
              />
            )}
            {isMissingRequired &&
              (showValidationErrors || touchedParameters[parameter.name]) && (
                <p className="text-xs text-red-500">
                  This value is required to deploy.
                </p>
              )}
          </div>
        );
      })}
    </div>
  );
};
